import React, { useCallback, useEffect, useRef, useState } from 'react'
import { string, bool } from 'prop-types'
import styled from 'styled-components'
import { useApolloClient } from '@apollo/client'

import {
  useAuthenticationState,
  useAuthenticationDispatch,
} from '../../contexts/AuthenticationContext'
import { NotificationContextProvider } from '../../contexts/NotificationContext'
import AuthenticationService from '../../services/ApiService/AuthenticationService'
import { baseTiltifyUrl } from '../../utils'
import { Modal } from '../Modal'
import { AuthenticationFlow } from './AuthenticationFlow'
import { FullWindowLoading } from '../Loading'

const StyledModal = styled(Modal)(
  ({ theme }) => `
    background-color: ${theme.isDark ? theme.darkGrey : theme.white};
    font-family: Work Sans;
    box-shadow: none;
    width: 350px;
`
)

export const Authentication = ({
  noModal,
  defaultSignUp,
  forceAuth,
  redirectUrl,
  preview,
  ...rest
}) => {
  const { showAuthentication, user: authedUser, refetch } = useAuthenticationState()
  const authenticationDispatch = useAuthenticationDispatch()
  const [firstLoad, setFirstLoad] = useState(true)
  const [redirectLoad, setRedirectLoad] = useState(true)
  const childWindow = useRef()
  const client = useApolloClient()

  const closeChild = (event) => {
    if (childWindow.current && event.data === 'closeTiltifyLogin') {
      childWindow.current.close()
      if (refetch) refetch()
    }
  }

  const openChild = useCallback((url) => {
    const height = 500
    const width = 300
    const y = window.outerHeight / 2 + window.screenY - height / 2
    const x = window.outerWidth / 2 + window.screenX - width / 2

    childWindow.current = window.open(
      url,
      'tiltifyWindow',
      `toolbar=no, location=no, directories=no, status=no, menubar=no, scrollbars=no, resizable=no, copyhistory=no, width=${width}, height=${height}, top=${y}, left=${x}`
    )
  }, [])

  useEffect(() => {
    setFirstLoad(false)
  }, [])

  useEffect(() => {
    if (authedUser && !preview) {
      if (redirectUrl) {
        window.location.replace(redirectUrl)
      } else {
        onClose()
      }
    } else {
      if (redirectLoad) setRedirectLoad(false)
    }
  }, [authedUser])

  useEffect(() => {
    window.addEventListener('message', closeChild, false)
    return () => {
      window.removeEventListener('message', closeChild)
    }
  }, [openChild])

  const onModalClose = () => {
    const service = new AuthenticationService()
    service
      .signOutUser()
      .then((result) => {
        authenticationDispatch({
          type: useAuthenticationDispatch.ACTION_TYPES.CLEAR_USER,
          user: null,
        })
        client.resetStore()
      })
      .catch((err) => {
        throw err
      })

    onClose(false)
  }

  const onClose = (valid = true) => {
    authenticationDispatch({
      type: useAuthenticationDispatch.ACTION_TYPES.HIDE_AUTHENTICATION,
    })

    if (noModal) window.location.replace(redirectUrl)

    if (forceAuth && !valid) {
      window.location.replace(baseTiltifyUrl())
    }

    try {
      window.scroll({
        top: 0,
        left: 0,
        behavior: 'smooth',
      })
    } catch (error) {
      window.scrollTo(0, 0)
    }
  }

  useEffect(() => {
    if (!authedUser && forceAuth && !showAuthentication && !firstLoad && authenticationDispatch) {
      authenticationDispatch({
        type: useAuthenticationDispatch.ACTION_TYPES.SHOW_AUTHENTICATION,
        showLogin: !defaultSignUp,
      })
    }
  })

  if (redirectUrl && redirectLoad) return <FullWindowLoading />
  if (!showAuthentication) return null

  if (noModal) {
    return (
      <NotificationContextProvider>
        <AuthenticationFlow redirectUrl={redirectUrl} onClose={onClose} {...rest} />
      </NotificationContextProvider>
    )
  }

  return (
    <StyledModal setIsVisible={onModalClose} isVisible skipOutsideClick={forceAuth}>
      <NotificationContextProvider>
        <AuthenticationFlow
          redirectUrl={redirectUrl}
          onClose={onClose}
          childWindow={childWindow}
          closeChild={closeChild}
          openChild={openChild}
          {...rest}
        />
      </NotificationContextProvider>
    </StyledModal>
  )
}

Authentication.propTypes = {
  defaultSignUp: bool,
  redirectUrl: string,
  forceAuth: bool,
  noModal: bool,
  preview: bool,
}

Authentication.defaultProps = {
  defaultSignUp: false,
  forceAuth: false,
  noModal: false,
  preview: false,
}
